/**
 * /help's Budget & MMM rows: how each MMM figure is made, its 90% range, the readiness and quality thresholds, staleness
 * and auto-allocate. Thresholds come from budgetContract and BREAK_EVEN, the range's ends from rangeOf itself, and every
 * label through D5's own key. The "4 weeks" of the current budget is the trainer's CURRENT_WEEKS (bundle.py), which
 * the app cannot import. Pure.
 *
 * @module components/help/helpBudget
 */
import type { TFunction } from 'i18next';
import { BREAK_EVEN } from '@/components/budget/ContributionTable';
import { metricLabel } from '@/components/budget/ReadinessCard';
import { MAX_SHIFT, OUTCOME_METRICS, QUALITY, READINESS, STALE_DAYS } from '@/services/budget/budgetContract';
import { rangeOf } from '@/services/budget/curves';
import { isCount } from '@/services/budget/outcomeUnits';
import { DASH, fmtPct } from '@/services/overview/overviewFormat';
import type { HelpTerm } from './helpContent';

/** Over 0…100 each end of rangeOf is the percentile it takes. */
const RANGE = rangeOf(Array.from({ length: 101 }, (_, i) => i));

/** The seven checks, each in D5's own label with how its value is counted. */
const readinessLines = (t: TFunction): string[] => {
  const line = (label: string, rule: string): string => t('help.budget.readiness.line', { label, rule });
  const metrics = OUTCOME_METRICS.map((m) => metricLabel(m, t)).join(' / ');
  return [
    line(t('budget.check.history', { n: READINESS.historyWeeks }), t('help.budget.readiness.history')),
    line(t('budget.check.overlap', { n: READINESS.overlapWeeks }), t('help.budget.readiness.overlap')),
    line(t('budget.check.channels', { n: READINESS.minChannels }), t('help.budget.readiness.channels')),
    line(
      t('budget.check.continuity', { n: READINESS.continuityDays }),
      t('help.budget.readiness.continuity', { n: READINESS.continuityDays }),
    ),
    line(
      t('budget.check.outcome', { metric: metrics, pct: fmtPct(READINESS.outcomeCoverage, 0) }),
      t('help.budget.readiness.outcome'),
    ),
    line(t('budget.check.scope'), t('help.budget.readiness.scope')),
    line(t('budget.check.currency'), t('help.budget.readiness.currency')),
  ];
};

export const budgetOf = (t: TFunction): HelpTerm[] => {
  // Leads, Bookings and Sales each name their own unit, so the columns are printed with "…" in its place.
  const unit = '…';
  const pct = Math.round(RANGE.hi - RANGE.lo);
  const shift = fmtPct(MAX_SHIFT, 0);
  return [
    {
      term: t('budget.contribution.col.contribution'),
      formula: t('help.budget.contribution.formula'),
      note: t('help.budget.contribution.note', { share: t('budget.contribution.col.share'), baseline: t('budget.decomposition.baseline') }),
    },
    {
      term: t('budget.contribution.col.roi'),
      formula: t('help.budget.roi.formula'),
      note: t('help.budget.roi.note', {
        counts: OUTCOME_METRICS.filter(isCount).map((m) => metricLabel(m, t)).join(', '),
        avgCost: t('budget.contribution.col.avgCost', { unit }),
      }),
    },
    {
      term: t('budget.contribution.col.mroas'),
      formula: t('help.budget.mroas.formula'),
      note: t('help.budget.mroas.note', {
        even: BREAK_EVEN, good: t('budget.contribution.good'), below: t('budget.contribution.below'), dash: DASH,
        nextCost: t('budget.contribution.col.nextCost', { unit }),
      }),
    },
    { term: t('budget.curves.title'), formula: t('help.budget.curves.formula'), note: t('help.budget.curves.note') },
    {
      term: t('budget.simulator.reset'),
      formula: t('help.budget.current.formula'),
      note: t('help.budget.current.note', { stopped: t('budget.stopped'), interim: t('budget.interim.title') }),
    },
    {
      term: t('help.budget.range.term', { pct }),
      formula: t('help.budget.range.formula', { lo: Math.round(RANGE.lo), hi: Math.round(RANGE.hi) }),
      note: t('help.budget.range.note', { pct, over: t('budget.cost.over', { amount: '…' }) }),
    },
    {
      term: t('help.budget.quality.term'),
      formula: t('help.budget.quality.formula', {
        r2: t('budget.status.tile.r2'), minR2: QUALITY.minR2.toFixed(2), mape: t('budget.status.tile.mape'),
        maxMape: fmtPct(QUALITY.maxMape, 0), maxRhat: QUALITY.maxRhat.toFixed(2), maxDivergences: QUALITY.maxDivergences,
      }),
      note: t('help.budget.quality.note', { dontDecide: t('budget.status.dontDecide') }),
    },
    { term: t('budget.status.subtitle'), lines: readinessLines(t) },
    { term: t('budget.status.stale'), formula: t('help.budget.stale.formula', { days: STALE_DAYS }) },
    {
      term: t('budget.simulator.auto', { pct: shift }),
      formula: t('help.budget.auto.formula', { pct: shift }),
      note: t('help.budget.auto.note'),
    },
  ];
};
